import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {DeleteOutlined, EditOutlined, EnvironmentOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Divider, Row, Table, TableColumnsType, Tooltip, Typography} from 'antd';

import LocationManagementDrawer from './LocationManagementDrawer';
import {useGetAllLocationsQuery} from '../../../store/api/locationsApi';
import PageTitle from '../../../components/page/PageTitle';
import ErrorBoundary from '../../../components/ErrorBoundary';
import {BaseError} from '../../../types/apiTypes';
import {Location} from '../../../types/locationTypes';
import {messageType, showMessages} from '../../../helpers/message';
import {sanitizeHTML} from '../../../helpers/misc';

import './LocationsView.less';

const LocationsView = () => {
    const mobileMode = window.innerWidth < 500;
    const navigate = useNavigate();
    const location = useLocation();

    const [page, setPage] = React.useState<number>(location?.state?.page ?? 1);
    const [perPage, setPerPage] = React.useState<number>(location?.state?.perPage ?? 10);
    const [total, setTotal] = React.useState<number>(10);

    const [action, setAction] = React.useState<'Add' | 'Update'>('Add');
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [currentLocation, setCurrentLocation] = React.useState<Location | null>(null);

    const {data: allLocations, isFetching, isSuccess, isError, error} = useGetAllLocationsQuery({page, perPage, sort: 'name'});

    React.useEffect(() => {
        if (isSuccess && allLocations) {
            setTotal(allLocations.total);
        } else if (isError) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }, [error, isError, isSuccess, allLocations]);

    const handleClose = () => {
        setCurrentLocation(null);
        setAction('Add')
        setDrawerOpen(false);
    }

    const onChange = (page: number, perPage: number) => {
        setPage(page);
        setPerPage(perPage);
    }

    const renderActionsColumn = (_text: string, record: Location) => {
        return (
            <div className="location-view-action-btn-container">
                <EditOutlined
                    key="edit"
                    onClick={() => {
                        setDrawerOpen(true);
                        setCurrentLocation(record);
                        setAction('Update');
                    }}
                />
                <Divider type="vertical"/>
                <Tooltip
                    className="tool-tip"
                     trigger="hover"
                     placement="top"
                     title="Not yet implemented."
                >
                    <DeleteOutlined key="delete" className="anticon-disabled"/>
                </Tooltip>
            </div>
        )
    }

    const renderExpandedMobileRow = React.useCallback((record: Location) => {
        // In mobile size show description and action columns as rows.
        const expandRows = [
            mobileMode && <li key={0} className="location-view-expanded-row-list-item">
                <Typography.Text className="location-view-expanded-row-list-title" strong>Description: </Typography.Text>
                {record.description ?? 'N/A'}
            </li>,
            <li key={1} className="location-view-expanded-row-list-item">
                <Typography.Text className="location-view-expanded-row-list-title" strong>Latitude: </Typography.Text>
                {record.latitude ?? 'N/A'}
            </li>,
            <li key={2} className="location-view-expanded-row-list-item">
                <Typography.Text className="location-view-expanded-row-list-title" strong>Longitude: </Typography.Text>
                {record.longitude ?? 'N/A'}
            </li>,
            <li key={3} className="location-view-expanded-row-list-item">
                <Typography.Text className="location-view-expanded-row-list-title" strong>Parking Note: </Typography.Text>
                {record.public_note ? sanitizeHTML(record.public_note) : 'N/A'}
            </li>,
            <li key={4} className="location-view-expanded-row-list-item">
                <Typography.Text className="location-view-expanded-row-list-title" strong>Staff Location Note: </Typography.Text>
                {record.private_note ? sanitizeHTML(record.private_note) : 'N/A'}
            </li>,
            mobileMode && <li key={5} className="location-view-expanded-action-buttons-row">
                <Typography.Text className="location-view-expanded-row-list-title" strong>Actions: </Typography.Text>
                {renderActionsColumn('', record)}
            </li>,
        ];
        return (
            <ul className="location-view-expanded-row-list">
                {expandRows}
            </ul>
        )
    }, [mobileMode]);

    let columns: TableColumnsType<Location> = [
        {
            title: <Typography.Text strong>Name</Typography.Text>,
            dataIndex: 'name',
            key: 'name',
            render: (name, location) => {
                return (
                    <span
                        className="location-row-name"
                        onClick={() => navigate('/fleet/location?id=' + location.id, {state: {page, perPage}})}
                    >
                        {name}
                    </span>
                )
            },
        },
        {
            title: <Typography.Text strong>Short Description</Typography.Text>,
            dataIndex: 'description',
            key: 'description',
            render: (description) => {
                return <span className="long-text-wrap">{description ?? 'N/A'}</span>
            }
        },
        {
            title:  <Typography.Text strong>Action</Typography.Text>,
            dataIndex: undefined,
            key: 'action',
            width: '90px',
            colSpan: 2,
            render: renderActionsColumn,
        }
    ];

    // In mobile keep Name column, move Description and Actions to expandable rows.
    columns = mobileMode ? [columns[0]] : columns;

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total,
        onChange,
        showSizeChanger: true,
        simple: window.innerWidth <= 767,
        showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} items`,
        onShowSizeChange: onChange,
    };

    const LocationListHeader = (
        <Row className="location-view-header-row">
            <Typography.Title className="location-view-list-title" level={5}>Locations</Typography.Title>
            <Button
                type="primary"
                icon={<PlusOutlined/>}
                onClick={() => setDrawerOpen(true)}
            >
                Add New
            </Button>
        </Row>
    );

    return (
        <ErrorBoundary>
            <div className="title-wrapper">
                <PageTitle title="All Locations" icon={<EnvironmentOutlined/>}/>
            </div>
            <LocationManagementDrawer
                currentLocation={currentLocation}
                action={action}
                drawerOpen={drawerOpen}
                handleClose={handleClose}
            />
            <Table
                rowKey="id"
                dataSource={allLocations?.data}
                expandable={{expandedRowRender: renderExpandedMobileRow}}
                columns={columns}
                loading={isFetching}
                pagination={paginationAttr}
                caption={LocationListHeader}
            />
        </ErrorBoundary>
    );
}

export default LocationsView;
